import { useRef, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const REQUIRED_KEYS = ["users", "customers", "vendors", "jobs", "purchases", "cash_transfers", "categories"];

export default function DataBackup() {
  const { logout } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [parsedPayload, setParsedPayload] = useState(null);
  const [fileName, setFileName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = useRef(null);

  // ---- Export ----
  const onExport = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" });
      triggerDownload(blob, `penz-yedek-${todayStamp()}.json`);
      toast.success("Yedek dosyası indirildi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yedek alınamadı");
    } finally {
      setDownloading(false);
    }
  };

  // ---- Import ----
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow reselecting the same file later
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error("Lütfen geçerli bir .json yedek dosyası seçin");
      return;
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.meta || parsed.meta.app !== "brea-cari") {
        toast.error("Bu dosya bir Penz yedek dosyası değil");
        return;
      }
      const missing = REQUIRED_KEYS.filter((k) => !Array.isArray(parsed[k]));
      if ((missing ?? []).length > 0) {
        toast.error(`Eksik alanlar: ${missing.join(", ")}`);
        return;
      }
      if (!(parsed.users ?? []).length) {
        toast.error("Yedek dosyasında kullanıcı kaydı yok");
        return;
      }
      setParsedPayload(parsed);
      setFileName(file.name);
      setConfirmOpen(true);
    } catch {
      toast.error("Dosya okunamadı: geçersiz JSON");
    }
  };

  const onConfirmRestore = async () => {
    if (!parsedPayload) return;
    setConfirmOpen(false);
    setRestoring(true);
    try {
      const { data } = await api.post("/backup/import", parsedPayload);
      const counts = data?.counts || {};
      const total = Object.values(counts ?? {}).reduce((s, n) => s + n, 0);
      toast.success(`Veriler başarıyla geri yüklendi. (${total} kayıt)`);
      // Force re-login: the user record may have a different password hash now.
      setTimeout(() => {
        toast.info("Lütfen tekrar giriş yapın.");
        logout();
      }, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geri yükleme başarısız");
    } finally {
      setRestoring(false);
      setParsedPayload(null);
      setFileName("");
    }
  };

  return (
    <SectionCard title="Veri Yedeği">
      <div className="p-5 space-y-6" data-testid="data-backup-section">
        {/* ---- Export ---- */}
        <div className="space-y-3">
          <div className="text-sm text-slate-600 leading-relaxed">
            Tüm verilerinizi (müşteriler, tedarikçiler, işler, satın almalar, ödemeler,
            kasa transferleri, kategoriler, notlar ve kullanıcı bilgileri) tek bir JSON
            dosyası olarak indirin. Uygulamayı başka bir sunucuya taşırken bu dosyayı
            kullanabilirsiniz.
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-xs text-slate-500">
            Dosya adı: <span className="font-mono text-slate-700">penz-yedek-{todayStamp()}.json</span>
          </div>
          <Button
            onClick={onExport}
            disabled={downloading}
            className="bg-slate-900 hover:bg-slate-800 rounded-lg"
            data-testid="export-backup-button"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Hazırlanıyor..." : "Yedeği İndir (JSON)"}
          </Button>
        </div>

        <div className="border-t border-slate-200" />

        {/* ---- Import / Restore ---- */}
        <div className="space-y-3" data-testid="data-restore-section">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-800">Yedekten Geri Yükle</h4>
            <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
              Dikkat
            </span>
          </div>
          <div className="text-sm text-slate-600 leading-relaxed">
            Önceden indirdiğiniz bir <span className="font-mono">.json</span> yedek dosyasını
            yükleyerek tüm verileri geri yükleyebilirsiniz. İşlem başlamadan önce mevcut
            verilerinizin otomatik bir yedeği alınır.
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Bu işlem mevcut veritabanındaki <strong>tüm</strong> verileri silecek ve
              seçilen yedek dosyasındaki verilerle değiştirecektir. Geri yükleme sonrası
              tekrar giriş yapmanız gerekir.
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={onFileChosen}
            className="hidden"
            data-testid="restore-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={restoring}
            variant="outline"
            className="rounded-lg"
            data-testid="restore-backup-button"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {restoring ? "Geri yükleniyor..." : "Yedek Dosyası Seç"}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Geri yüklemek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                <strong>{fileName}</strong> dosyasından veriler geri yüklenecek.
              </span>
              <span className="block text-amber-700">
                Bu işlem mevcut veritabanındaki tüm verileri silecek ve seçilen yedek
                dosyasındaki verileri geri yükleyecektir. Devam etmek istiyor musunuz?
              </span>
              {parsedPayload?.meta?.generated_at && (
                <span className="block text-xs text-slate-500">
                  Yedek tarihi: {parsedPayload.meta.generated_date || parsedPayload.meta.generated_at}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-button">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmRestore}
              className="bg-red-600 hover:bg-red-700"
              data-testid="restore-confirm-button"
            >
              Evet, Geri Yükle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}
